import { createContext, useContext, useEffect } from "react";
import socket from "../services/socket";
import { useSelector } from "react-redux";
import { selectAuthData } from "../core/store/redux/slices/Auth-slice";
import { Helper } from "../utils/Helpers";
const { showNotification } = new Helper();

const SocketContext = createContext(socket);

const getMessagePreviewText = (message, messageType) => {
  if (!message && !messageType) return 'No messages yet';

  switch (messageType) {
    case 'image':
      return '📷 Photo';
    case 'document': {
      const docMatch = message?.match(/Document: (.+)/) || message?.match(/\[Document: (.+?)\]/);
      const docName = docMatch ? docMatch[1] : '📄 Document';
      return `📄 ${docName}`;
    }
    case 'video':
      return '🎥 Video';
    case 'audio':
      return '🎵 Audio';
    default:
      return message || 'New message';
  }
};

export const SocketProvider = ({ children, navigate }) => {
  const { hotel } = useSelector(selectAuthData);
  const hotel_key = hotel?.hotel_key;

  useEffect(() => {
    if (!socket) {
      return;
    }

    if (!hotel_key) {
      console.log("No hotel_key found, not connecting socket");
      return;
    }

    socket.connect();

    const handleChatNotificationClick = (e) => {
      const customerNumber = e.detail.customerNumber;
      console.log("Received chat notification click", customerNumber);
      localStorage.setItem("chat_redirect_number", customerNumber);
      if (navigate) {
        navigate("/main/customer-support");
      }
    };
  
    window.addEventListener("chatNotificationClick", handleChatNotificationClick);

    const handleConnect = () => {
      console.log("Socket connected:", socket.id);
      socket.emit("join_hotel", hotel_key);
    };

    const handleDisconnect = () => {
      console.log("Socket disconnected");
    };

    const handleReconnect = () => {
      console.log("Socket reconnected:", socket.id);
      socket.emit("join_hotel", hotel_key);
    };

    const handleNewMessageForNotification = (messageData) => {
      try {
        if (!messageData.inbound) return;

        const messageText =
          messageData.message ||
          (messageData.message_type && `[${messageData.message_type.toUpperCase()}]`);

        const senderName =
          messageData.customer_name || messageData.customer_number || 'New Message';

        const customerNumber = messageData.customer_number;

        showNotification(
          `New message from ${senderName}`,
          getMessagePreviewText(messageData.message, messageData.message_type),
          '/favicon.ico',
          () => {
            console.log("Notification clicked for", messageData.customer_number);
            const event = new CustomEvent("chatNotificationClick", {
              detail: { customerNumber: messageData.customer_number }
            });
            window.dispatchEvent(event);
          }
        );
      } catch (err) {
        console.error("Notification error in SocketContext:", err);
      }
    };

    socket.on("new_message", handleNewMessageForNotification);
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("reconnect", handleReconnect);

    if (socket.connected) {
      socket.emit("join_hotel", hotel_key);
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("reconnect", handleReconnect);
      socket.off("new_message", handleNewMessageForNotification);
      window.removeEventListener("chatNotificationClick", handleChatNotificationClick);
      socket.disconnect();
    };
  }, [hotel_key]);

  return (
    <SocketContext.Provider value={socket}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);